import React, {
  createRef,
  ForwardedRef,
  forwardRef,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent,
  PropsWithChildren,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { cx } from '@emotion/css';
import { Hint, HintType } from '../index';
import { Tooltip } from '../../tooltip/tooltip';
import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';

const circleInfoIcon = getDesignSystemIcon('icon-circle-tooltip.svg');

export interface MenuItem {
  name: string;
  value: string | number | readonly string[] | undefined;
  disabled?: boolean;
  tooltip?: {
    title?: string;
    body: string;
    id: string;
  };
}

export interface SelectProps<T> {
  className?: string;
  withButton?: boolean;
  buttonLabel?: string;
  buttonIcon?: string;
  onButtonClick?: (e: MouseEvent<HTMLButtonElement>) => void;
  hint?: { type: HintType; message: string };
  menuItems: MenuItem[];
  disabled?: boolean;
  placeholder?: string;
  required?: boolean;
  label?: string;
  defaultValue?: string | number | readonly string[] | undefined;
  selectedValue?: string | number | readonly string[] | undefined;
  tabIndex?: number;
  ref: ForwardedRef<unknown>;
  description?: string;
  onChange?: (value: string | number | readonly string[] | undefined) => void;
}

/**
 * @deprecated You should use the Dropdown component or the HookFormSelect when used in a React Hook form
 */
export const Select = forwardRef(function Select<T>(
  {
    menuItems,
    disabled = false,
    placeholder,
    label,
    hint,
    description,
    required = false,
    tabIndex = -1,
    defaultValue,
    onChange,
    className = '',
    withButton = false,
    buttonLabel = '',
    buttonIcon = '',
    onButtonClick,
    selectedValue,
    ...props
  }: PropsWithChildren<SelectProps<T>>,
  ref
) {
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState<string | number | readonly string[] | undefined>(selectedValue || defaultValue || placeholder);
  const [hoverItem, setHoverItem] = useState<MenuItem | null>(null);
  const [headerClasses, setHeaderClasses] = useState<string[]>([]);

  const node = useRef<HTMLDivElement>(null);
  const hoverItemRef = useRef<MenuItem | null>(hoverItem);
  const selectHeaderInputRef: RefObject<HTMLInputElement> = createRef();

  const selectedItem = useMemo(
    () => menuItems.find((item) => item.value === value) || ({ name: placeholder, value: '' } as MenuItem),
    [menuItems, placeholder, value]
  );

  useEffect(() => {
    if (selectedValue) {
      setValue(selectedValue);
    }
  }, [menuItems, selectedValue]);

  useEffect(() => {
    const classes: string[] = ['relative w-full bg-primary-white outline-none'];

    if (disabled) {
      classes.push('border text-secondary-mid border-secondary-mid cursor-not-allowed');
    } else if (isOpen) {
      classes.push('border-2 border-secondary-deep');
    } else if (hint?.type === 'error') {
      classes.push('border-2 border-red');
    } else {
      classes.push('border border-secondary-light');
    }

    if (!disabled) {
      classes.push('group-hover:border-secondary-deep group-focus:border-secondary-deep');
    }

    setHeaderClasses(classes);
  }, [isOpen, disabled, hint?.type]);

  useEffect(() => {
    // handle keyboard events
    const supportKeyboardNavigation = (event: KeyboardEvent) => {
      if (isOpen) {
        const currentHoverIndex = menuItems.findIndex((item) => item === hoverItemRef.current);
        let item = hoverItemRef.current;
        switch (event.code) {
          case 'ArrowDown':
            item = menuItems[currentHoverIndex + 1 === menuItems.length ? 0 : currentHoverIndex + 1];
            break;
          case 'ArrowUp':
            item = menuItems[currentHoverIndex - 1 < 0 ? menuItems.length - 1 : currentHoverIndex - 1];
            break;
          case 'Space':
          case 'Enter':
            if (hoverItemRef.current) {
              onClickMenuItem(hoverItemRef.current);
            }
            break;
        }
        if (item) {
          setHoverItem(item);
          hoverItemRef.current = item;
        }
        event.preventDefault();
      }
    };

    if (isOpen) {
      window.addEventListener('mousedown', onOutsideClick);
      window.addEventListener('keydown', supportKeyboardNavigation);
    }
    return () => {
      window.removeEventListener('mousedown', onOutsideClick);
      window.removeEventListener('keydown', supportKeyboardNavigation);
    };
  }, [isOpen, menuItems]);

  const toggleDropdown = (e?: React.MouseEvent<HTMLDivElement>) => {
    e?.stopPropagation();
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const onClickMenuItem = (item: MenuItem) => {
    if (item.disabled) {
      return;
    }
    setValue(item.value);
    setHoverItem(null);
    toggleDropdown();

    if (onChange) {
      onChange(item.value);
    }
  };

  // Hides the text-selection (Chrome automatically selects the text when the input is focused via the tab key)
  const hideTextSelectionOfInputWhenFocused = () => {
    selectHeaderInputRef?.current?.setSelectionRange(0, 0);
  };

  /* Why ArrowUp, ArrowDown, Space & Escape?
   *
   * To be consistent with the native select, we need to toggle the open state when using the up
   * and down arrow keys and the space bar. However, the arrow keys only need to open the dropdown
   * if the dropdown is closed. The escape key only closes the dropdown.
   */
  const handleSelectHeaderKeyDownEvents = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    if (!isOpen && (event.code === 'ArrowUp' || event.code === 'ArrowDown')) {
      setIsOpen(true);
    } else if (isOpen && event.code === 'Escape') {
      setIsOpen(false);
    } else if (event.code === 'Space') {
      toggleDropdown();
    }
  };

  const renderHeader = () => {
    return (
      <div onClick={toggleDropdown} className="group" data-testid="select-header">
        <input
          type="text"
          data-testid={`selectedValue${selectedItem?.value || ''}`}
          inputMode={'none'}
          readOnly={true}
          unselectable={'on'}
          className={cx('h-12 !cursor-pointer select-none py-3', selectedItem?.tooltip ? 'pl-4 pr-16' : 'px-4', headerClasses, className)}
          ref={selectHeaderInputRef}
          value={selectedItem?.name}
          disabled={disabled ? disabled : undefined}
          onFocus={hideTextSelectionOfInputWhenFocused}
          onKeyDown={handleSelectHeaderKeyDownEvents}
        />
        <div className={`absolute right-4 top-[calc(50%-0.375rem)] w-3 ${disabled ? 'cursor-not-allowed' : '!cursor-pointer'}`}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 16 16"
            fill="currentColor"
            className={cx(
              'w-full transform text-primary-grey transition-transform duration-200',
              isOpen ? '-rotate-180' : 'rotate-0',
              { 'text-secondary': disabled },
              className
            )}
          >
            <path d="M8.01 13.06.02 5.07l1.41-1.42 6.58 6.58 6.57-6.58L16 5.07z" />
          </svg>
        </div>
      </div>
    );
  };

  const renderTooltip = (item: MenuItem) => {
    return (
      item.tooltip && (
        <Tooltip
          body={item.tooltip.body}
          id={item.tooltip.id}
          maxWidth={15}
          icon={
            <svg
              className={cx('ml-2 inline-block h-4 w-4', {
                'text-primary-black': !item.disabled || item.value === selectedItem?.value,
                'text-primary-grey': item.disabled,
              })}
            >
              <use xlinkHref={`${circleInfoIcon}#img`} />
            </svg>
          }
        ></Tooltip>
      )
    );
  };

  const renderList = () => {
    return (
      <div
        data-testid="select-list"
        className="absolute z-10 max-h-52 min-w-full whitespace-nowrap border-2 border-solid border-secondary-deep bg-primary-white text-sm"
        style={{
          top: 'calc(100% - 2px)',
        }}
      >
        <div className="mb-2 mt-1 max-h-52 cursor-pointer overflow-y-auto">
          {menuItems.map((item) => (
            <div
              key={item.name}
              onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                e.stopPropagation();
                onClickMenuItem(item);
              }}
              onMouseEnter={() => !item.disabled && setHoverItem(item)}
              onMouseLeave={() => setHoverItem(null)}
              className={cx(
                'px-4 py-2',
                item === hoverItem && 'bg-secondary-light',
                item === selectedItem && 'font-bold',
                item.disabled && 'text-primary-grey'
              )}
              data-testid={item.value}
            >
              {item.name}
              <span> {renderTooltip(item)}</span>
            </div>
          ))}
        </div>
        {withButton && (
          <div className="mr-4 flex items-center justify-between border-t border-primary-grey pl-4 pr-4">
            <button className="pb-4 pt-4 stihl-text-interactive-text" onClick={(e) => (onButtonClick ? onButtonClick(e) : undefined)}>
              {buttonLabel}
            </button>
            <img src={buttonIcon} alt="" className={`inline-block h-4 w-4 `} />
          </div>
        )}
      </div>
    );
  };

  const renderLabelAndDescription = () => {
    return (
      <>
        {label && (
          <div className={`mb-1 stihl-text-body-sm-reg ${disabled ? 'text-secondary' : ''}`}>{`${label} ${required ? '*' : ''}`}</div>
        )}
        {description && <div className="mb-1 text-xs">{description}</div>}
      </>
    );
  };

  // Handle outside click
  const onOutsideClick = (e: Event) => {
    if (!node.current || node?.current?.contains(e.target as Node)) {
      return;
    }

    setIsOpen(false);
  };

  return (
    <>
      {renderLabelAndDescription()}
      <div {...props} className="relative select-none stihl-text-body-default-reg" ref={node}>
        {renderHeader()}
        {menuItems.length > 0 && isOpen && renderList()}
      </div>
      {hint && <Hint {...hint} />}
      <select defaultValue={value} className="hidden" />
    </>
  );
});

export default Select;
